import ClearIcon from '@mui/icons-material/RemoveCircleOutlineRounded';
import { Box, Grid, IconButton, Link, Stack, Typography, useTheme } from '@mui/material';
import { useLocale } from '@repo/i18n';
import { capitalize } from '@repo/common-utils/TextUtils';
import SummaryPriceDetailsForCartItem from './SummaryPriceDetailsForCartItem';
import { useMemo, type JSX } from 'react';
import { SummaryCartItem } from './summary-mapping';

interface IProps {
    summaryCartItemsByType: Record<string, SummaryCartItem[]>;
    invertedColorsClass?: Record<string, unknown>;
    isUsingPaymentPlan: boolean;
    isLoadingPrices: boolean;
    onRemoveCartItem?: (summaryCartItem: SummaryCartItem) => void;
    onEditClick: (e: React.MouseEvent, summaryCartItem: SummaryCartItem) => void;
    enableItemEditing: boolean;
    enableCartClearing: boolean;
    onClearCart?: () => void;
}

export default function SummaryBooking(props: IProps): JSX.Element {
    const { summaryCartItemsByType, invertedColorsClass } = props;
    const { t } = useLocale();
    const theme = useTheme();

    const sectionsToShow = useMemo(() => {
        return Object.entries(summaryCartItemsByType).filter(([_, items]) => items.length > 0);
    }, [summaryCartItemsByType]);

    return (
        <Box>
            {sectionsToShow.map(([tKey, items], i) =>
                items.length === 0 ? null : (
                    <Grid mb={theme.spacing(4)} key={tKey}>
                        <Grid container justifyContent="space-between" alignItems="center">
                            <Typography
                                component="h4"
                                variant="h5"
                                color="textSecondary"
                                sx={invertedColorsClass}
                            >
                                {tKey}
                            </Typography>

                            {i === 0 && props.onClearCart && (
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={props.onClearCart}
                                    sx={{
                                        color: 'text.secondary',
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        fontSize: '0.875rem',
                                        background: 'none',
                                        border: 'none',
                                        ...props.invertedColorsClass,
                                    }}
                                >
                                    {t.empty_cart}
                                </Link>
                            )}
                        </Grid>
                        {items.map((item) => (
                            <SummaryBookingItem
                                key={item.uniqueKey}
                                summaryCartItem={item}
                                invertedColorsClass={invertedColorsClass}
                                onEditClick={
                                    props.enableItemEditing && item.supportsEdit
                                        ? (e) => {
                                              props.onEditClick(e, item);
                                          }
                                        : undefined
                                }
                                onClearClick={
                                    props.onRemoveCartItem
                                        ? () => props.onRemoveCartItem?.(item)
                                        : undefined
                                }
                                isLoadingPrices={props.isLoadingPrices}
                            />
                        ))}
                    </Grid>
                ),
            )}
        </Box>
    );
}

interface SummaryBookingItemProps {
    summaryCartItem: SummaryCartItem;
    invertedColorsClass?: Record<string, unknown>;
    onClearClick?: () => void;
    onEditClick?: (e: React.MouseEvent) => void;
    isLoadingPrices: boolean;
}

function SummaryBookingItem(props: SummaryBookingItemProps): JSX.Element {
    return (
        <Box>
            <SummaryHeaderForCartItem
                summaryCartItem={props.summaryCartItem}
                invertedColorsClass={props.invertedColorsClass}
                onClearClick={props.onClearClick}
            ></SummaryHeaderForCartItem>

            <SummaryPriceDetailsForCartItem
                invertedColorsClass={props.invertedColorsClass}
                summaryCartItem={props.summaryCartItem}
                onEditClick={props.onEditClick}
            />
        </Box>
    );
}

function SummaryHeaderForCartItem(props: {
    summaryCartItem: SummaryCartItem;
    invertedColorsClass?: Record<string, unknown>;
    onClearClick?: () => void;
}) {
    const { t } = useLocale();
    const theme = useTheme();
    const { summaryCartItem } = props;

    return (
        <Grid
            container
            direction="row"
            wrap="nowrap"
            justifyContent="space-between"
            borderBottom={`1px solid ${theme.palette.grey[400]}`}
            margin={theme.spacing(1, 0, 1.5, 0)}
        >
            <Stack justifyContent={summaryCartItem.dateInformation ? 'flex-end' : 'center'}>
                <Typography
                    component="h5"
                    variant="h6"
                    align="left"
                    color="textSecondary"
                    sx={props.invertedColorsClass}
                >
                    {summaryCartItem.displayTitle}
                </Typography>
                {summaryCartItem.dateInformation && (
                    <Typography align="left" color="textSecondary" sx={props.invertedColorsClass}>
                        {summaryCartItem.dateInformation}
                    </Typography>
                )}
            </Stack>
            {props.onClearClick && (
                <IconButton
                    aria-label={`
                            ${capitalize(t.remove_cart_item)}${' '}
                            ${summaryCartItem.displayTitle}
                        `}
                    color="secondary"
                    sx={{
                        height: 'fit-content',
                        marginRight: '-0.5em',
                        ...props.invertedColorsClass,
                    }}
                    onClick={props.onClearClick}
                >
                    <ClearIcon />
                </IconButton>
            )}
        </Grid>
    );
}
